// src/utils/generateReport.js
export function generateAndDownloadReport(result, userName) {
  if (!result) {
    console.error("generateReport: no result provided");
    return;
  }

  console.log("generateReport called with:", {
    ats_score: result.ats_score,
    sections: result.sections?.length,
    present_keywords: result.present_keywords?.length,
    missing_keywords: result.missing_keywords?.length,
    rewrites: result.rewrites?.length,
    top_recommendations: result.top_recommendations?.length,
  });

  // Safe getters with fallbacks
  const sections = Array.isArray(result.sections) ? result.sections : [];
  const presentKws = Array.isArray(result.present_keywords) ? result.present_keywords : [];
  const missingKws = Array.isArray(result.missing_keywords) ? result.missing_keywords : [];
  const rewrites = Array.isArray(result.rewrites) ? result.rewrites : [];
  const recommendations = Array.isArray(result.top_recommendations) ? result.top_recommendations : [];

  const scoreColor = result.ats_score >= 75 ? "#3B6D11" : result.ats_score >= 50 ? "#854F0B" : "#A32D2D";
  const scoreBg = result.ats_score >= 75 ? "#EAF3DE" : result.ats_score >= 50 ? "#FAEEDA" : "#FCEBEB";

  // ── Section rows HTML ──
  const sectionRowsHtml = sections.length > 0
    ? sections.map(s => {
        const color = s.score === "Good" ? "#3B6D11" : s.score === "Fair" ? "#854F0B" : "#A32D2D";
        const bg = s.score === "Good" ? "#EAF3DE" : s.score === "Fair" ? "#FAEEDA" : "#FCEBEB";
        return `<tr>
          <td style="padding:10px 14px;font-weight:500;border-bottom:1px solid #eee;font-size:14px;vertical-align:top;">${s.name || ""}</td>
          <td style="padding:10px 14px;border-bottom:1px solid #eee;vertical-align:top;">
            <span style="background:${bg};color:${color};padding:3px 12px;border-radius:20px;font-size:12px;font-weight:600;white-space:nowrap;">${s.score || "Fair"}</span>
          </td>
          <td style="padding:10px 14px;border-bottom:1px solid #eee;font-size:13px;color:#495057;line-height:1.7;vertical-align:top;">${s.feedback || ""}</td>
        </tr>`;
      }).join("")
    : `<tr><td colspan="3" style="padding:12px 14px;font-size:13px;color:#868E96;">No section feedback available.</td></tr>`;

  // ── Present keywords HTML ──
  const presentKwsHtml = presentKws.length > 0
    ? presentKws.map(k =>
        `<span style="background:#EAF3DE;color:#3B6D11;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:500;margin:3px;display:inline-block;">${k}</span>`
      ).join("")
    : `<span style="font-size:13px;color:#868E96;">None detected</span>`;

  // ── Missing keywords HTML ──
  const missingKwsHtml = missingKws.length > 0
    ? missingKws.map(k =>
        `<span style="background:#FCEBEB;color:#A32D2D;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:500;margin:3px;display:inline-block;">${k}</span>`
      ).join("")
    : `<span style="font-size:13px;color:#868E96;">No major gaps found</span>`;

  // ── Rewrites HTML ──
  const rewritesHtml = rewrites.length > 0
    ? rewrites.map(r => `
        <div style="margin-bottom:16px;padding:16px;border:1px solid #e9ecef;border-radius:10px;background:#fafafa;">
          ${r.section ? `<div style="font-size:11px;font-weight:600;color:#868E96;margin-bottom:8px;text-transform:uppercase;letter-spacing:0.05em;">${r.section}</div>` : ""}
          <div style="font-size:13px;color:#6c757d;padding:8px 12px;background:#f1f3f5;border-radius:6px;margin-bottom:8px;line-height:1.7;">${r.before || ""}</div>
          <div style="font-size:11px;color:#adb5bd;text-align:center;margin:6px 0;">↓ improved version</div>
          <div style="font-size:13px;color:#185FA5;padding:8px 12px;background:#E6F1FB;border-radius:6px;line-height:1.7;">${r.after || ""}</div>
        </div>`
      ).join("")
    : `<p style="font-size:13px;color:#868E96;padding:8px 0;">No rewrite suggestions available.</p>`;

  // ── Recommendations HTML ──
  const recommendationsHtml = recommendations.length > 0
    ? recommendations.map((r, i) => `
        <div style="padding:12px 16px;background:#f8f9fa;border-radius:8px;margin-bottom:8px;font-size:13px;line-height:1.7;display:flex;gap:12px;">
          <span style="color:#185FA5;font-weight:700;min-width:20px;">${i + 1}.</span>
          <span style="color:#343a40;">${r}</span>
        </div>`
      ).join("")
    : `<p style="font-size:13px;color:#868E96;padding:8px 0;">No recommendations available.</p>`;

  const reportDate = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric"
  });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>ATS Resume Analysis Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      color: #212529;
      background: #ffffff;
      font-size: 15px;
      line-height: 1.6;
    }
    .page { max-width: 820px; margin: 0 auto; padding: 48px 40px; }
    h2 {
      font-size: 17px;
      font-weight: 600;
      margin: 36px 0 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e9ecef;
      color: #212529;
    }
    table { width: 100%; border-collapse: collapse; }
    th {
      padding: 10px 14px;
      text-align: left;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #868E96;
      background: #f8f9fa;
      border-bottom: 2px solid #e9ecef;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 20px; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- ── HEADER ── -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;margin-bottom:32px;border-bottom:3px solid #185FA5;">
    <div>
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.1em;color:#185FA5;font-weight:700;margin-bottom:8px;">ResumeScoreAI</div>
      <h1 style="font-size:26px;font-weight:700;color:#212529;margin-bottom:8px;letter-spacing:-0.02em;">ATS Resume Analysis Report</h1>
      <p style="color:#868E96;font-size:13px;">Prepared for <strong style="color:#495057;">${userName || "User"}</strong> &bull; ${reportDate}</p>
    </div>
    <div style="text-align:center;background:${scoreBg};padding:20px 28px;border-radius:14px;min-width:130px;flex-shrink:0;">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:${scoreColor};font-weight:700;margin-bottom:6px;">ATS Score</div>
      <div style="font-size:52px;font-weight:700;color:${scoreColor};line-height:1;">${result.ats_score}</div>
      <div style="font-size:12px;color:${scoreColor};margin-top:4px;opacity:0.8;">out of 100</div>
    </div>
  </div>

  <!-- ── SCORE METRICS ── -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:28px;">
    <div style="background:#f8f9fa;border-radius:12px;padding:18px;text-align:center;">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:#868E96;font-weight:600;margin-bottom:8px;">Keyword Match Rate</div>
      <div style="font-size:36px;font-weight:700;color:#212529;">${result.match_rate}%</div>
      <div style="font-size:12px;color:#868E96;margin-top:4px;">of JD keywords found</div>
    </div>
    <div style="background:#FCEBEB;border-radius:12px;padding:18px;text-align:center;">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:#A32D2D;font-weight:600;margin-bottom:8px;">Missing Keywords</div>
      <div style="font-size:36px;font-weight:700;color:#A32D2D;">${result.missing_count}</div>
      <div style="font-size:12px;color:#A32D2D;margin-top:4px;">to add to resume</div>
    </div>
  </div>

  <!-- ── SCORE BAR ── -->
  <div style="height:10px;background:#e9ecef;border-radius:5px;overflow:hidden;margin-bottom:28px;">
    <div style="height:100%;width:${result.ats_score}%;background:${scoreColor};border-radius:5px;"></div>
  </div>

  <!-- ── SUMMARY ── -->
  ${result.summary ? `
  <div style="background:#E6F1FB;border-left:4px solid #185FA5;padding:16px 20px;border-radius:0 10px 10px 0;margin-bottom:32px;font-size:14px;color:#0C447C;line-height:1.8;">
    ${result.summary}
  </div>` : ""}

  <!-- ── SECTION FEEDBACK ── -->
  <h2>Section Feedback</h2>
  <table>
    <thead>
      <tr>
        <th style="width:22%;">Section</th>
        <th style="width:15%;">Score</th>
        <th>Feedback</th>
      </tr>
    </thead>
    <tbody>${sectionRowsHtml}</tbody>
  </table>

  <!-- ── KEYWORDS FOUND ── -->
  <h2>Keywords Found in Resume</h2>
  <div style="display:flex;flex-wrap:wrap;gap:4px;padding:6px 0;min-height:36px;">
    ${presentKwsHtml}
  </div>

  <!-- ── MISSING KEYWORDS ── -->
  <h2>Missing Keywords to Add</h2>
  <div style="display:flex;flex-wrap:wrap;gap:4px;padding:6px 0;min-height:36px;">
    ${missingKwsHtml}
  </div>

  <!-- ── SUGGESTED REWRITES ── -->
  <h2>Suggested Rewrites</h2>
  ${rewritesHtml}

  <!-- ── TOP RECOMMENDATIONS ── -->
  <h2>Top Recommendations</h2>
  ${recommendationsHtml}

  <!-- ── FOOTER ── -->
  <div style="margin-top:52px;padding-top:20px;border-top:1px solid #e9ecef;display:flex;justify-content:space-between;align-items:center;">
    <span style="font-size:12px;color:#adb5bd;">Generated by ResumeScoreAI &bull; resumescoreai.com</span>
    <span style="font-size:12px;color:#adb5bd;">${reportDate}</span>
  </div>

</div>
</body>
</html>`;

  // ── Download as HTML file ──
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ATS-Report-${(userName || "User").replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}